import React, { useContext, useEffect, useState } from 'react';

import classnames from 'classnames';

import { reloadWidgetData } from 'common/actions/changelogWidget';
import { UpdateHeightContext } from 'common/changelog/widget/ChangelogWidgetContainer';
import { ParamsContext } from 'common/containers/RouterContainer';
import connect from 'common/core/connect';
import translateString from 'common/i18n/translateString';
import Markdown from 'common/markdown/Markdown';
import ChangelogHeart, { type Props as ChangelogHeartProps } from 'common/reaction/ChangelogHeart';
import ReactionsMenu from 'common/reaction/ReactionsMenu';
import nbspLastSpace from 'common/util/nbspLastSpace';

import ChangelogWidgetHome from './ChangelogWidgetHome';

import type { Entry, EntryType } from 'common/api/endpoints/entries';
import type { Dispatch } from 'redux';

import 'css/components/changelog/widget/_ChangelogWidgetEntry.scss';

type Props = {
  entries: Entry[];
  fullscreenMode: boolean;
  isNavigatingBack: boolean;
  onEntryOpened: (entry: Entry) => void;
  updateHeight: () => void;
  readIDs: string[];
};

const ChangelogWidgetEntry = ({
  entries,
  fullscreenMode,
  isNavigatingBack,
  onEntryOpened,
  readIDs,
}: Props) => {
  const [entry, setEntry] = useState<Entry | undefined>(undefined);
  const updateHeight = useContext(UpdateHeightContext);
  const params = useContext(ParamsContext);

  useEffect(() => {
    updateHeight();

    const currentEntry = entries.find((entry: Entry) => {
      return entry.urlName === params?.entryURLName;
    });

    if (!currentEntry) {
      return;
    }

    setEntry(currentEntry);
    onEntryOpened(currentEntry);
  }, [entry, params.entryURLName, entries, onEntryOpened, updateHeight]);

  const onImageLoaded = () => {
    updateHeight();
  };

  if (!entry) {
    // should never get here
    return null;
  }

  return (
    <div
      className={classnames('changelogWidgetEntry', {
        fullscreenMode,
        navigatingBack: isNavigatingBack,
      })}>
      <ChangelogWidgetHome entries={entries} readIDs={readIDs} />
      <div className="changelogWidgetEntryContents">
        <div className="text">
          <h2 className="title" key="title">
            {nbspLastSpace(translateString(entry, 'title'))}
          </h2>
          <div className="types">
            {entry.types.map((type: EntryType) => {
              const className = classnames('type', `${type.toLowerCase()}Type`);
              return (
                <p className={className} key={type}>
                  {type}
                </p>
              );
            })}
          </div>
          <div className="details">
            <Markdown contents={translateString(entry, 'details')} onImageLoaded={onImageLoaded} />
            <div className="reactionsMenuContainer">
              <ReactionsMenu
                allowedReactions={{
                  like: ({ disabled, fill, stroke }: ChangelogHeartProps) => (
                    <ChangelogHeart disabled={disabled} fill={fill} stroke={stroke} />
                  ),
                }}
                authRedirectEnabled={false}
                object={entry}
                objectType="changelog"
                reloadObjectData={reloadWidgetData}
                showReactorNames={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(
  null,
  (
    dispatch: Dispatch<any>,
    { params: { companyID }, ssoToken }: { params: { companyID: string }; ssoToken: string },
  ) => ({
    reloadWidgetData: () => dispatch(reloadWidgetData(companyID, ssoToken, [])),
  }),
)(ChangelogWidgetEntry);
